import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { tryRefreshToken } from "@/api/auth";
import { evaluateAuthorization } from "@/lib/permissions";
import { NotFound } from "@/routes/root/NotFound";
import type { AuthContextValue } from "@/types/auth";

const RootLayout = lazy(() => import("@/routes/root/RootLayout"));
const Home = lazy(() => import("@/routes/home/Home"));
const Login = lazy(() => import("@/routes/login/Login"));
const AuthCallback = lazy(() => import("@/routes/auth/AuthCallback"));
const WebSocketDebug = lazy(() => import("@/routes/websocket-debug/WebSocketDebug"));
const ErrorFallback = lazy(() => import("@/routes/root/ErrorFallback"));

const RoleList = lazy(() => import("@/routes/access/roles/RoleList"));
const RoleCreate = lazy(() => import("@/routes/access/roles/RoleCreate"));
const RoleDetail = lazy(() => import("@/routes/access/roles/RoleDetail"));
const UserList = lazy(() => import("@/routes/users/UserList"));
const UserDetail = lazy(() => import("@/routes/users/UserDetail"));
const PermissionReference = lazy(() => import("@/routes/access/permissions/PermissionReference"));
const RouteMapping = lazy(() => import("@/routes/access/routes/RouteMapping"));

interface RouterContext {
  auth: AuthContextValue;
}

export const PUBLIC_ROUTES = ["/login", "/auth/callback"];

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: ErrorFallback,
  beforeLoad: async ({ location, context }) => {
    if (PUBLIC_ROUTES.includes(location.pathname)) {
      return;
    }

    context.auth.setAuthLoading(true);

    try {
      const ok = await tryRefreshToken(context.auth.setIdentity);

      if (!ok) {
        throw redirect({ to: "/login" });
      }
    } finally {
      context.auth.setAuthLoading(false);
    }
  },
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: Home,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: Login,
});

const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth/callback",
  component: AuthCallback,
});

const wsDebugRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/websocket-debug",
  component: WebSocketDebug,
});

const roleListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/access/roles",
  component: RoleList,
  beforeLoad: ({ context }) => {
    if (!evaluateAuthorization(context.auth.hasPermission, ["system:manage_roles"])) {
      throw redirect({ to: "/" });
    }
  },
});

const roleCreateRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/access/roles/create",
  component: RoleCreate,
  beforeLoad: ({ context }) => {
    if (!evaluateAuthorization(context.auth.hasPermission, ["system:manage_roles"])) {
      throw redirect({ to: "/" });
    }
  },
});

const roleDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/access/roles/$roleName",
  component: RoleDetail,
  beforeLoad: ({ context }) => {
    if (!evaluateAuthorization(context.auth.hasPermission, ["system:manage_roles"])) {
      throw redirect({ to: "/" });
    }
  },
});

const userListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users",
  component: UserList,
  beforeLoad: ({ context }) => {
    if (!evaluateAuthorization(context.auth.hasPermission, ["admin:list_users"])) {
      throw redirect({ to: "/" });
    }
  },
});

const userDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/users/$userId",
  component: UserDetail,
  beforeLoad: ({ context }) => {
    if (!evaluateAuthorization(context.auth.hasPermission, ["admin:view_user"])) {
      throw redirect({ to: "/" });
    }
  },
});

const permissionReferenceRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/access/permissions",
  component: PermissionReference,
  beforeLoad: ({ context }) => {
    if (
      !evaluateAuthorization(context.auth.hasPermission, [
        "system:manage_roles",
        "user:manage_permissions",
      ])
    ) {
      throw redirect({ to: "/" });
    }
  },
});

const routeMappingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/access/routes",
  component: RouteMapping,
  beforeLoad: ({ context }) => {
    if (!evaluateAuthorization(context.auth.hasPermission, ["system:manage_roles"])) {
      throw redirect({ to: "/" });
    }
  },
});

const routeTree = rootRoute.addChildren([
  homeRoute,
  loginRoute,
  authCallbackRoute,
  wsDebugRoute,
  roleListRoute,
  roleCreateRoute,
  roleDetailRoute,
  userListRoute,
  userDetailRoute,
  permissionReferenceRoute,
  routeMappingRoute,
]);

export const createAppRouter = (context: RouterContext) =>
  createRouter({ routeTree, context, defaultNotFoundComponent: NotFound });
